/**
 * ContactClientModal — Send SMS or Email to a client
 * Features:
 *  - Client search bar: type to search any client from DB, auto-fills phone + email
 *  - SMS tab: sends via Zapier → Textellent bridge, saves phone to DB
 *  - Email tab: sends via Gmail SMTP, saves email to DB
 *  - All sends logged to client_notifications (audit trail)
 */
import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { MessageSquare, Mail, Phone, Edit2, Check, Search, X } from "lucide-react";

interface ContactClientModalProps {
  open: boolean;
  onClose: () => void;
  clientName?: string;
  clientEmail?: string | null;
  clientPhone?: string | null;
  senderName: string;
  senderEmail: string;
  /** User role — tax_division → HRBBS number, others → APXGS number */
  userRole?: string;
}

export function ContactClientModal({
  open,
  onClose,
  clientName: initialClientName = "",
  clientEmail: initialClientEmail,
  clientPhone: initialClientPhone,
  senderName,
  senderEmail,
  userRole,
}: ContactClientModalProps) {
  const fromLabel: "APXGS" | "HRBBS" = userRole === "tax_division" ? "HRBBS" : "APXGS";
  const [tab, setTab] = useState<"sms" | "email">("sms");

  // ── Client search state ──────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState(initialClientName);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(initialClientName);
  const searchRef = useRef<HTMLDivElement>(null);

  // ── Contact fields ────────────────────────────────────────────────────────
  const [phone, setPhone] = useState(initialClientPhone ?? "");
  const [editingPhone, setEditingPhone] = useState(!initialClientPhone);
  const [email, setEmail] = useState(initialClientEmail ?? "");
  const [editingEmail, setEditingEmail] = useState(!initialClientEmail);

  // ── Message state ─────────────────────────────────────────────────────────
  const [smsMessage, setSmsMessage] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const utils = trpc.useUtils();

  // Reset when modal opens with new client
  useEffect(() => {
    if (open) {
      setSearchQuery(initialClientName);
      setSelectedClient(initialClientName);
      setPhone(initialClientPhone ?? "");
      setEditingPhone(!initialClientPhone);
      setEmail(initialClientEmail ?? "");
      setEditingEmail(!initialClientEmail);
      setSmsMessage("");
      setEmailSubject("");
      setEmailBody("");
      setShowDropdown(false);
    }
  }, [open, initialClientName, initialClientPhone, initialClientEmail]);

  // ── Search query (debounced via enabled flag) ─────────────────────────────
  const { data: searchResults = [] } = trpc.communications.searchClients.useQuery(
    { query: searchQuery },
    { enabled: showDropdown && searchQuery.trim().length >= 1 }
  );

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSelectClient = (result: { clientName: string; clientPhone?: string | null; clientEmail?: string | null }) => {
    setSelectedClient(result.clientName);
    setSearchQuery(result.clientName);
    setPhone(result.clientPhone ?? "");
    setEditingPhone(!result.clientPhone);
    setEmail(result.clientEmail ?? "");
    setEditingEmail(!result.clientEmail);
    setShowDropdown(false);
  };

  const handleClearClient = () => {
    setSelectedClient("");
    setSearchQuery("");
    setPhone("");
    setEmail("");
    setEditingPhone(true);
    setEditingEmail(true);
    setShowDropdown(false);
  };

  // ── tRPC mutations ────────────────────────────────────────────────────────
  const sendSms = trpc.communications.sendSms.useMutation({
    onSuccess: () => {
      toast.success(`SMS sent to ${selectedClient}`);
      setSmsMessage("");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const sendEmail = trpc.communications.sendEmail.useMutation({
    onSuccess: () => {
      toast.success(`Email sent to ${selectedClient}`);
      setEmailSubject("");
      setEmailBody("");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const updatePhone = trpc.communications.updateClientPhone.useMutation({
    onSuccess: () => setEditingPhone(false),
    onError: (err) => toast.error(err.message),
  });

  const updateEmail = trpc.communications.updateClientEmail.useMutation({
    onSuccess: () => setEditingEmail(false),
    onError: (err) => toast.error(err.message),
  });

  const handleSendSms = () => {
    if (!phone.trim()) { toast.error("Phone number is required"); return; }
    if (!smsMessage.trim()) { toast.error("Message is required"); return; }
    // Only save phone to DB if a client was selected from the dropdown
    if (selectedClient.trim() && phone !== initialClientPhone) {
      updatePhone.mutate({ clientName: selectedClient, clientPhone: phone.trim() });
    }
    const label = selectedClient.trim() || searchQuery.trim() || phone.trim();
    sendSms.mutate({ senderName, senderEmail, clientName: label, clientPhone: phone.trim(), message: smsMessage.trim(), fromLabel });
  };

  const handleSendEmail = () => {
    if (!email.trim()) { toast.error("Email address is required"); return; }
    if (!emailSubject.trim()) { toast.error("Subject is required"); return; }
    if (!emailBody.trim()) { toast.error("Message body is required"); return; }
    // Only save email to DB if a client was selected from the dropdown
    if (selectedClient.trim() && email !== initialClientEmail) {
      updateEmail.mutate({ clientName: selectedClient, clientEmail: email.trim() });
    }
    const label = selectedClient.trim() || searchQuery.trim() || email.trim();
    sendEmail.mutate({ senderName, senderEmail, clientName: label, clientEmail: email.trim(), subject: emailSubject.trim(), body: emailBody.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.28 0.04 240)", color: "oklch(0.12 0.04 240)" }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.12 0.04 240)" }}>
            Contact Client
          </DialogTitle>
        </DialogHeader>

        {/* ── Client Search ─────────────────────────────────────────────── */}
        <div ref={searchRef} className="relative mb-2">
          <Label className="text-sm mb-1 block" style={{ color: "oklch(0.28 0.04 240)" }}>Client <span className="font-normal text-xs" style={{ color: "oklch(0.45 0.03 240)" }}>(optional — search or type name)</span></Label>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "oklch(0.28 0.04 240)" }} />
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedClient("");
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search DB or type any name…"
                className="pl-8 text-sm"
                style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)", color: "oklch(0.12 0.04 240)" }}
              />
            </div>
            {searchQuery && (
              <Button size="icon" variant="ghost" onClick={handleClearClient} className="h-8 w-8 shrink-0">
                <X className="w-3.5 h-3.5" style={{ color: "oklch(0.28 0.04 240)" }} />
              </Button>
            )}
          </div>

          {/* Dropdown results */}
          {showDropdown && searchResults.length > 0 && (
            <div
              className="absolute z-50 w-full mt-1 rounded-md shadow-lg overflow-hidden"
              style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)" }}
            >
              {searchResults.map((r) => (
                <button
                  key={r.clientName}
                  className="w-full text-left px-3 py-2 text-sm transition-colors"
                  style={{ color: "oklch(0.12 0.04 240)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "oklch(0.28 0.04 240)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  onClick={() => handleSelectClient(r)}
                >
                  <span className="font-medium">{r.clientName}</span>
                  <span className="ml-2 text-sm" style={{ color: "oklch(0.28 0.04 240)" }}>
                    {r.clientPhone ? `📱 ${r.clientPhone}` : "no phone"}
                    {r.clientEmail ? ` · ✉ ${r.clientEmail}` : ""}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Selected client badge */}
          {selectedClient && (
            <p className="text-sm mt-1" style={{ color: "oklch(0.08 0.01 240)" }}>
              ✓ {selectedClient}
            </p>
          )}
        </div>

        {/* ── SMS / Email Tabs ──────────────────────────────────────────── */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as "sms" | "email")}>
          <TabsList className="w-full" style={{ background: "oklch(0.97 0.01 220)" }}>
            <TabsTrigger value="sms" className="flex-1 gap-2">
              <MessageSquare className="w-4 h-4" />SMS
            </TabsTrigger>
            <TabsTrigger value="email" className="flex-1 gap-2">
              <Mail className="w-4 h-4" />Email
            </TabsTrigger>
          </TabsList>

          {/* SMS Tab */}
          <TabsContent value="sms" className="space-y-4 mt-4">
            <div className="space-y-1">
              <Label className="text-sm" style={{ color: "oklch(0.28 0.04 240)" }}>Phone Number</Label>
              <div className="flex gap-2 items-center">
                {editingPhone ? (
                  <>
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 (310) 555-0000"
                      className="flex-1 text-sm"
                      style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)", color: "oklch(0.12 0.04 240)" }}
                    />
                    <Button size="icon" variant="ghost" onClick={() => setEditingPhone(false)} className="h-8 w-8">
                      <Check className="w-4 h-4 text-[oklch(0.35_0.01_240)]" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)" }}>
                      <Phone className="w-3.5 h-3.5" style={{ color: "oklch(0.28 0.04 240)" }} />
                      <span>{phone || <span style={{ color: "oklch(0.28 0.04 240)" }}>No phone on file</span>}</span>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => setEditingPhone(true)} className="h-8 w-8">
                      <Edit2 className="w-3.5 h-3.5" style={{ color: "oklch(0.28 0.04 240)" }} />
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-sm" style={{ color: "oklch(0.28 0.04 240)" }}>Message</Label>
              <Textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Type your SMS message…"
                rows={4}
                maxLength={1600}
                style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)", color: "oklch(0.12 0.04 240)", resize: "none" }}
              />
              <p className="text-sm text-right" style={{ color: "oklch(0.28 0.04 240)" }}>{smsMessage.length}/1600</p>
            </div>

            <Button
              className="w-full gap-2"
              onClick={handleSendSms}
              disabled={sendSms.isPending}
              style={{ background: "oklch(0.82 0.01 240)", color: "oklch(0.12 0.04 240)" }}
            >
              <MessageSquare className="w-4 h-4" />
              {sendSms.isPending ? "Sending…" : "Send SMS"}
            </Button>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email" className="space-y-4 mt-4">
            <div className="space-y-1">
              <Label className="text-sm" style={{ color: "oklch(0.28 0.04 240)" }}>Client Email</Label>
              <div className="flex gap-2 items-center">
                {editingEmail ? (
                  <>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="client@example.com"
                      type="email"
                      className="flex-1 text-sm"
                      style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)", color: "oklch(0.12 0.04 240)" }}
                    />
                    <Button size="icon" variant="ghost" onClick={() => setEditingEmail(false)} className="h-8 w-8">
                      <Check className="w-4 h-4 text-[oklch(0.35_0.01_240)]" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)" }}>
                      <Mail className="w-3.5 h-3.5" style={{ color: "oklch(0.28 0.04 240)" }} />
                      <span className="truncate">{email || <span style={{ color: "oklch(0.28 0.04 240)" }}>No email on file</span>}</span>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => setEditingEmail(true)} className="h-8 w-8">
                      <Edit2 className="w-3.5 h-3.5" style={{ color: "oklch(0.28 0.04 240)" }} />
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-sm" style={{ color: "oklch(0.28 0.04 240)" }}>Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Subject…"
                style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)", color: "oklch(0.12 0.04 240)" }}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-sm" style={{ color: "oklch(0.28 0.04 240)" }}>Message</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Type your email message…"
                rows={5}
                style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(0.32 0.04 240)", color: "oklch(0.12 0.04 240)", resize: "none" }}
              />
            </div>

            <p className="text-sm" style={{ color: "oklch(0.28 0.04 240)" }}>
              Sent from: apxadvisor@gmail.com
            </p>

            <Button
              className="w-full gap-2"
              onClick={handleSendEmail}
              disabled={sendEmail.isPending}
              style={{ background: "oklch(0.82 0.01 240)", color: "oklch(0.12 0.04 240)" }}
            >
              <Mail className="w-4 h-4" />
              {sendEmail.isPending ? "Sending…" : "Send Email"}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
